import {FC} from 'react';

import {Box, styled, Typography, TypographyProps} from '@mui/material';

import {
  DeliveryIconExpressV2,
  DeliveryIconNextDaysV2,
  DeliveryIconOtherDaysV2,
  DeliveryIconPreOrderV2,
  DeliveryIconSameDaysV2
} from 'assets/icons/recommendation-icons/delivery-icons-v2';

import {ProductDeliveryInfoV2Props} from './types';

const colors = {
  red: '#FD1E1B',
  expressRed: '#FF003D',
  expressRedDim: '#FF5C83',
  cyan: '#1B969E',
  today: '#FA4147'
} as const;

interface StyledWrapperRootProps extends TypographyProps {
  color: ProductDeliveryInfoV2Props['variant'];
}

const StyledWrapperRoot = styled(Typography, {
  shouldForwardProp: (prop) => !(prop == 'color')
})<StyledWrapperRootProps>(({theme, color}) => ({
  fontSize: 12,
  fontWeight: 600,
  display: 'flex',
  width: 'auto',
  alignItems: 'center',
  position: 'relative',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  whiteSpace: 'nowrap',
  justifyContent: 'flex-start',
  gap: theme.spacing(0.5),
  ...(color === 'express' && {
    color: theme.palette.common.white,
    borderRadius: 10,
    paddingLeft: 20,
    paddingRight: theme.spacing(0.5)
  }),
  ...(color === 'same-day-v2' && {color: colors.today}),
  ...(color === 'other-day-v2' && {color: colors.cyan}),

  [theme.breakpoints.down('sm')]: {
    fontSize: 11,
    ...(color === 'express' && {
      paddingLeft: 16
    })
  }
})) as typeof Typography;

const StyledExpressIconWrapper = styled(Box)(({theme}) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  textAlign: 'center',
  position: 'absolute',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  left: -2,
  top: -1,
  border: `1px solid ${theme.palette.common.white}`,
  [theme.breakpoints.down('sm')]: {
    width: 16,
    height: 16,
    top: -1
  }
})) as typeof Box;

const StyledExpressExtended = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#FFDCDE',
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5)
})) as typeof Box;

const StyledExpressText = styled(Typography)(({theme}) => ({
  color: colors.red,
  fontSize: theme.spacing(1.5),
  fontWeight: theme.typography.fontWeightBold
})) as typeof Box;

const ProductDeliveryInfoViewV2: FC<ProductDeliveryInfoV2Props> = ({variant, text, hasExpressAndNormal}) => {
  switch (variant) {
    case 'express': {
      return (
        <StyledWrapperRoot data-testid="delivery-info-express" color="express">
          <StyledExpressIconWrapper component="span">
            <DeliveryIconExpressV2 />
          </StyledExpressIconWrapper>
          <StyledExpressText>{text || '3 Hours'}</StyledExpressText>
          {hasExpressAndNormal && (
            <StyledExpressExtended>
              <StyledExpressText>+1</StyledExpressText>
            </StyledExpressExtended>
          )}
        </StyledWrapperRoot>
      );
    }
    case 'same-day': {
      return (
        <StyledWrapperRoot data-testid="delivery-info-same-day" color="same-day-v2">
          <DeliveryIconSameDaysV2 />
          {text || 'Today'}
        </StyledWrapperRoot>
      );
    }
    case 'next-day': {
      return (
        <StyledWrapperRoot data-testid="delivery-info-next-day" color="other-day-v2">
          <DeliveryIconNextDaysV2 />
          {text || 'Next Day'}
        </StyledWrapperRoot>
      );
    }
    case 'pre-order': {
      return (
        <StyledWrapperRoot data-testid="delivery-info-pre-order" color="other-day-v2">
          <DeliveryIconPreOrderV2 />
          {text || 'Pre Order'}
        </StyledWrapperRoot>
      );
    }
    case 'days-min':
    case 'days-max': {
      return (
        <StyledWrapperRoot data-testid="delivery-info-bulky" color="other-day-v2">
          <DeliveryIconOtherDaysV2 />
          {text || 'Bulky'}
        </StyledWrapperRoot>
      );
    }
    default: {
      return (
        <StyledWrapperRoot data-testid="delivery-info-not-available" color="other-day-v2">
          <Box width={21} height={16} />
        </StyledWrapperRoot>
      );
    }
  }
};

ProductDeliveryInfoViewV2.displayName = 'MakroUi.ProductDeliveryInfoView';

export default ProductDeliveryInfoViewV2;
